"use client";

import type React from "react";

import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Text,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { TbCalendarSearch } from "react-icons/tb";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface CalendarAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const CalendarAvailabilityModal: React.FC<
  CalendarAvailabilityModalProps
> = ({ isOpen, onClose, onSave, initialValues }) => {
  const [name, setName] = useState("check_calendar_availability");
  const [description, setDescription] = useState(
    "When users ask for availability, check the calendar and provide available slots."
  );
  const [apiKey, setApiKey] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");
  const [timezone, setTimezone] = useState("");
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const [nameError, setNameError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "check_calendar_availability");
      setDescription(
        initialValues.description ||
          "When users ask for availability, check the calendar and provide available slots."
      );
      setApiKey(initialValues.cal_api_key || "");
      // Handle both property names for backward compatibility
      setEventTypeId(
        initialValues.event_type_id?.toString() ||
          initialValues.cal_event_type_id ||
          ""
      );
      setTimezone(initialValues.timezone || "America/Los_Angeles");
    } else {
      // Reset to defaults when adding a new function
      setName("check_calendar_availability");
      setDescription(
        "When users ask for availability, check the calendar and provide available slots."
      );
      setApiKey("");
      setEventTypeId("");
      setTimezone("America/Los_Angeles");
    }
  }, [initialValues]);

  const validateToolName = (name: string) => {
    if (!name.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }

    if (name.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }

    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onSave({
      type: "check_availability_cal",
      name,
      description,
      cal_api_key: apiKey,
      event_type_id: Number(eventTypeId), // Convert to number
      timezone,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        textColor={isDark ? "white" : "gray.800"}
      >
        <ModalHeader style={{ display: "flex", alignItems: "center" }}>
          <TbCalendarSearch size={20} style={{ marginRight: "8px" }} />
          <span>Check Calendar Availability (Cal.com)</span>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel>Name</FormLabel>
              <Input
                id="check-calendar-availability-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter tool name"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>
            <FormControl>
              <FormLabel>Description (Optional)</FormLabel>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                id="check-calendar-availability-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>API Key (Cal.com)</FormLabel>
              <Input
                id="check-calendar-availability-function-api-key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter Cal.com API key"
                type="password"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Event Type ID (Cal.com)</FormLabel>
              <Input
                id="check-calendar-availability-function-event-type-id"
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
                placeholder="Enter Event Type ID"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              <Text
                fontSize="sm"
                color={isDark ? "gray.400" : "gray.600"}
                mt={1}
              >
                Must be a numeric value (e.g., 2505265)
              </Text>
            </FormControl>
            <FormControl>
              <FormLabel>Timezone (Optional)</FormLabel>
              <Input
                id="check-calendar-availability-function-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Enter Timezone (e.g., America/Los_Angeles)"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              <Text
                fontSize="sm"
                color={isDark ? "gray.400" : "gray.600"}
                mt={1}
              >
                Default: America/Los_Angeles
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSave}
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
